import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useTheme,
  useMediaQuery,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as GetAppIcon,
  FilterList as FilterListIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  AccountCircle as AccountCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import tesoreriaCajaService, {
  InformacionCaja,
  MovimientoCaja,
  DetalleCajaResponse,
  CajaResumen,
  CajaSearchResponse,
  InformacionArqueo,
} from '../../services/tesoreriaCajaService';
import tesoreriaService, { GrupoCajaChica } from '../../services/tesoreriaService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import * as XLSX from 'xlsx';


const MovimientosCaja: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [codigoCaja, setCodigoCaja] = useState<number | ''>('');
  const [informacionCaja, setInformacionCaja] = useState<InformacionCaja | null>(null);
  const [detallesCaja, setDetallesCaja] = useState<DetalleCajaResponse | null>(null);
  const [arqueoCaja, setArqueoCaja] = useState<InformacionArqueo | null>(null);
  const [grupoNombre, setGrupoNombre] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetalles, setLoadingDetalles] = useState(false);
  const [loadingArqueo, setLoadingArqueo] = useState(false);
  const [movimientosAnteriores, setMovimientosAnteriores] = useState<MovimientoCaja[]>([]);

  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tipoFiltro, setTipoFiltro] = useState<'TODOS' | 'INGRESO' | 'EGRESO'>('TODOS');
  const [medioPagoFiltro, setMedioPagoFiltro] = useState<string>('');
  const [currentTab, setCurrentTab] = useState(0); // Tab de movimientos (Todos/Ingresos/Egresos)
  const [mainTab, setMainTab] = useState(0); // Tab principal (Arqueo/Movimientos)
  const [arqueoExpandido, setArqueoExpandido] = useState<Record<string, boolean>>({
    porTipoPago: true, // Solo "Por Tipo Pago" expandido por defecto
  });

  const [sortField, setSortField] = useState<keyof MovimientoCaja>('nombreMCP');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [selectedMovimiento, setSelectedMovimiento] = useState<MovimientoCaja | null>(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);

  const [openBuscarCajaDialog, setOpenBuscarCajaDialog] = useState(false);
  const [buscarCajaTerm, setBuscarCajaTerm] = useState('');
  const [buscarCajaResultados, setBuscarCajaResultados] = useState<CajaResumen[]>([]);
  const [buscarCajaEstado, setBuscarCajaEstado] = useState<string>('');
  const [buscarCajaGrupo, setBuscarCajaGrupo] = useState<string>('');
  const [buscarCajaFechaDesde, setBuscarCajaFechaDesde] = useState<string>('');
  const [buscarCajaFechaHasta, setBuscarCajaFechaHasta] = useState<string>('');
  const [buscarCajaPage, setBuscarCajaPage] = useState(0);
  const [buscarCajaRowsPerPage, setBuscarCajaRowsPerPage] = useState(10);
  const [buscarCajaTotal, setBuscarCajaTotal] = useState(0);
  const [buscarCajaLoading, setBuscarCajaLoading] = useState(false);
  const [buscarCajaGrupos, setBuscarCajaGrupos] = useState<GrupoCajaChica[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const cargarInformacionCaja = async (codigo: number, grupoNombreParam?: string | null): Promise<boolean> => {
    try {
      setLoading(true);
      const tieneAcceso = await tesoreriaCajaService.tieneAccesoACaja(codigo);
      if (!tieneAcceso) {
        setInformacionCaja(null);
        setDetallesCaja(null);
        setGrupoNombre(null);
        enqueueSnackbar('No tiene permisos para visualizar esta caja.', { variant: 'warning' });
        return false;
      }
      const info = await tesoreriaCajaService.getInformacionCaja(codigo);
      setInformacionCaja(info);
      
      // Si se proporciona el grupoNombre como parámetro, usarlo directamente
      if (grupoNombreParam) {
        setGrupoNombre(grupoNombreParam);
      } else {
        // Buscar el grupo de la caja para obtener el nombre
        try {
          const cajaSearch = await tesoreriaCajaService.searchCajas({
            pageNumber: 1,
            pageSize: 50,
            searchTerm: codigo.toString(),
          });
          const cajaEncontrada = cajaSearch.items.find(c => c.codigoCaja === codigo);
          if (cajaEncontrada?.grupoNombre) {
            setGrupoNombre(cajaEncontrada.grupoNombre);
          } else {
            setGrupoNombre(null);
          }
        } catch (error) {
          console.error('Error al cargar nombre del grupo:', error);
          setGrupoNombre(null);
        }
      }
      
      return true;
    } catch (error: any) {
      console.error('Error al cargar información de caja:', error);
      if (error?.response?.status === 404) {
        enqueueSnackbar('Caja no encontrada', { variant: 'error' });
      } else {
        enqueueSnackbar('Error al cargar información de la caja', { variant: 'error' });
      }
      setInformacionCaja(null);
      setGrupoNombre(null);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const cargarDetallesCaja = async (codigo: number, pageNumber: number = 1, pageSize: number = 10) => {
    try {
      setLoadingDetalles(true);
      // Determinar el tipo de movimiento según el tab seleccionado
      let tipoMovimiento: 'Ingreso' | 'Egreso' | undefined = undefined;
      if (currentTab === 1) {
        tipoMovimiento = 'Ingreso';
      } else if (currentTab === 2) {
        tipoMovimiento = 'Egreso';
      }
      
      // Cargar solo la página actual del servidor con filtro de tipo de movimiento
      const detalles = await tesoreriaCajaService.getDetalleCaja(codigo, pageNumber, pageSize, tipoMovimiento);
      setDetallesCaja(detalles);
      
      // Si no estamos en la primera página, cargar todos los movimientos anteriores para calcular el saldo acumulado
      if (pageNumber > 1) {
        const movimientosAnterioresArray: MovimientoCaja[] = [];
        // Cargar todas las páginas anteriores
        for (let p = 1; p < pageNumber; p++) {
          const response = await tesoreriaCajaService.getDetalleCaja(codigo, p, pageSize, tipoMovimiento);
          movimientosAnterioresArray.push(...response.items);
        }
        // Ordenar por fecha para asegurar el orden correcto
        movimientosAnterioresArray.sort((a, b) => {
          const dateA = new Date(a.fechaEmision).getTime();
          const dateB = new Date(b.fechaEmision).getTime();
          return dateA - dateB;
        });
        setMovimientosAnteriores(movimientosAnterioresArray);
      } else {
        // Si estamos en la primera página, no hay movimientos anteriores
        setMovimientosAnteriores([]);
      }
    } catch (error: any) {
      console.error('Error al cargar detalles de caja:', error);
      if (error?.response?.status === 404) {
        enqueueSnackbar('No se encontraron movimientos para esta caja', { variant: 'warning' });
      } else {
        enqueueSnackbar('Error al cargar los movimientos de la caja', { variant: 'error' });
      }
      setDetallesCaja(null);
      setMovimientosAnteriores([]);
    } finally {
      setLoadingDetalles(false);
    }
  };

  const cargarArqueoCaja = async (codigo: number) => {
    try {
      setLoadingArqueo(true);
      const arqueo = await tesoreriaCajaService.getInformacionArqueo(codigo);
      setArqueoCaja(arqueo);
    } catch (error: any) {
      console.error('Error al cargar arqueo de caja:', error);
      if (error?.response?.status === 404) {
        enqueueSnackbar('No se encontró información de arqueo para esta caja', { variant: 'warning' });
      } else {
        enqueueSnackbar('Error al cargar el arqueo de la caja', { variant: 'error' });
      }
      setArqueoCaja(null);
    } finally {
      setLoadingArqueo(false);
    }
  };

  const handleBuscar = () => {
    if (!codigoCaja || (typeof codigoCaja === 'string' && codigoCaja === '')) {
      enqueueSnackbar('Ingrese un código de caja', { variant: 'warning' });
      return;
    }
    const codigo = typeof codigoCaja === 'number' ? codigoCaja : Number(codigoCaja);
    if (isNaN(codigo) || codigo <= 0) {
      enqueueSnackbar('Ingrese un código de caja válido', { variant: 'warning' });
      return;
    }
    setPage(0); // Resetear a la primera página
    cargarInformacionCaja(codigo);
    cargarDetallesCaja(codigo, 1, rowsPerPage);
    cargarArqueoCaja(codigo);
  };

  // Recargar cuando cambie el tab (resetear a página 1)
  useEffect(() => {
    if (codigoCaja && typeof codigoCaja === 'number' && informacionCaja) {
      setPage(0);
      setMovimientosAnteriores([]); // Limpiar movimientos anteriores al cambiar de tab
      cargarDetallesCaja(codigoCaja, 1, rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  // Recargar cuando cambie la página
  useEffect(() => {
    if (codigoCaja && typeof codigoCaja === 'number' && informacionCaja && page >= 0) {
      cargarDetallesCaja(codigoCaja, page + 1, rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleIrCajaAbierta = async () => {
    try {
      const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
      if (!saved) {
        // Si no hay grupo seleccionado, abrir el buscador filtrado por cajas ABIERTAS
        handleAbrirBuscarCajaDialog('ABIERTO', true);
        // Ejecutar búsqueda automáticamente con el estado ABIERTO directamente
        setTimeout(() => {
          ejecutarBusquedaCajas(0, buscarCajaRowsPerPage, 'ABIERTO', '', '');
        }, 0);
        return;
      }

      let grupo: number | null = null;
      try {
        const parsed = JSON.parse(saved);
        grupo = Number(parsed?.codigo ?? parsed?.id ?? null);
      } catch {
        grupo = null;
      }

      if (!grupo || !Number.isFinite(grupo) || grupo <= 0) {
        // Grupo inválido: comportarse como si no hubiera grupo y abrir buscador de cajas abiertas
        handleAbrirBuscarCajaDialog('ABIERTO', true);
        setTimeout(() => {
          ejecutarBusquedaCajas(0, buscarCajaRowsPerPage, 'ABIERTO', '', '');
        }, 0);
        return;
      }

      const cajaAbierta = await tesoreriaService.getCajaAbiertaPorGrupo(grupo);
      const codigo =
        cajaAbierta?.codigoCaja ??
        cajaAbierta?.nroCaja ??
        cajaAbierta?.codigo ??
        (cajaAbierta as any)?.id ??
        null;

      if (!codigo || Number(codigo) <= 0) {
        enqueueSnackbar('No se encontró una caja abierta para el grupo seleccionado.', {
          variant: 'info',
        });
        return;
      }

      const codigoNum = Number(codigo);
      setCodigoCaja(codigoNum);
      // Cargar información de la caja (valida acceso)
      const infoCargada = await cargarInformacionCaja(codigoNum);
      // Si la carga fue exitosa, cargar también arqueo y detalles
      if (infoCargada) {
        await Promise.all([
          cargarArqueoCaja(codigoNum),
          cargarDetallesCaja(codigoNum, 1, rowsPerPage)
        ]);
      }
    } catch (error) {
      console.error('Error al ir a caja abierta:', error);
      enqueueSnackbar('Error al buscar la caja abierta', { variant: 'error' });
    }
  };

  const handleAbrirBuscarCajaDialog = (estadoPredefinido?: string, limpiarFechas: boolean = false) => {
    setOpenBuscarCajaDialog(true);
    setBuscarCajaResultados([]);
    setBuscarCajaTerm('');
    setBuscarCajaEstado(estadoPredefinido || '');
    setBuscarCajaGrupo('');

    if (limpiarFechas) {
      // Limpiar fechas si se solicita
      setBuscarCajaFechaDesde('');
      setBuscarCajaFechaHasta('');
    } else {
      // Fechas por defecto: últimos 3 meses hasta hoy
      const hoy = new Date();
      const fin = hoy.toISOString().split('T')[0];
      const inicioDate = new Date(hoy);
      inicioDate.setMonth(inicioDate.getMonth() - 3);
      const inicio = inicioDate.toISOString().split('T')[0];
      setBuscarCajaFechaDesde(inicio);
      setBuscarCajaFechaHasta(fin);
    }
    setBuscarCajaPage(0);
    setBuscarCajaTotal(0);
    // Cargar grupos de caja para el filtro
    tesoreriaService
      .getGruposCajaChica()
      .then((grupos) => {
        setBuscarCajaGrupos(grupos || []);
      })
      .catch((error) => {
        console.error('Error al cargar grupos de caja para filtro:', error);
      });
  };

  const handleAbrirBuscarCajaDialogDefault = () => {
    handleAbrirBuscarCajaDialog();
  };

  const ejecutarBusquedaCajas = async (
    page: number, 
    pageSize: number,
    estadoOverride?: string,
    fechaDesdeOverride?: string,
    fechaHastaOverride?: string
  ) => {
    const term = buscarCajaTerm.trim();
    try {
      setBuscarCajaLoading(true);
      const response: CajaSearchResponse = await tesoreriaCajaService.searchCajas({
        pageNumber: page + 1,
        pageSize,
        searchTerm: term || undefined,
        estado: estadoOverride !== undefined ? estadoOverride : (buscarCajaEstado || undefined),
        grupo: buscarCajaGrupo ? Number(buscarCajaGrupo) : undefined,
        fechaAperturaDesde: fechaDesdeOverride !== undefined 
          ? (fechaDesdeOverride ? `${fechaDesdeOverride}T00:00:00` : undefined)
          : (buscarCajaFechaDesde ? `${buscarCajaFechaDesde}T00:00:00` : undefined),
        fechaAperturaHasta: fechaHastaOverride !== undefined
          ? (fechaHastaOverride ? `${fechaHastaOverride}T23:59:59` : undefined)
          : (buscarCajaFechaHasta ? `${buscarCajaFechaHasta}T23:59:59` : undefined),
        soloCajasDelUsuario: true,
      });
      setBuscarCajaResultados(response.items);
      setBuscarCajaTotal(response.totalCount);
      setBuscarCajaPage(response.pageNumber - 1);
      setBuscarCajaRowsPerPage(response.pageSize);
      if (!response.items.length) {
        enqueueSnackbar('No se encontraron cajas con ese criterio', { variant: 'info' });
      }
    } catch (error) {
      console.error('Error al buscar cajas:', error);
      enqueueSnackbar('Error al buscar cajas', { variant: 'error' });
    } finally {
      setBuscarCajaLoading(false);
    }
  };

  const handleBuscarCaja = async () => {
    if (
      !buscarCajaTerm.trim() &&
      !buscarCajaEstado &&
      !buscarCajaGrupo.trim() &&
      !buscarCajaFechaDesde &&
      !buscarCajaFechaHasta
    ) {
      enqueueSnackbar('Ingrese un criterio o seleccione al menos un filtro para buscar cajas', {
        variant: 'warning',
      });
      return;
    }
    await ejecutarBusquedaCajas(0, buscarCajaRowsPerPage);
  };

  const handleSeleccionarCaja = async (caja: CajaResumen) => {
    const codigo = caja.codigoCaja;
    setCodigoCaja(codigo);
    setOpenBuscarCajaDialog(false);
    // Cargar información de la caja (valida acceso) pasando el grupoNombre si está disponible
    const infoCargada = await cargarInformacionCaja(codigo, caja.grupoNombre || null);
    // Si la carga fue exitosa, cargar también arqueo y detalles
    if (infoCargada) {
      await Promise.all([
        cargarArqueoCaja(codigo),
        cargarDetallesCaja(codigo, 1, rowsPerPage)
      ]);
    }
  };

  const handleRefresh = () => {
    if (codigoCaja && typeof codigoCaja === 'number') {
      cargarInformacionCaja(codigoCaja);
      cargarArqueoCaja(codigoCaja);
      cargarDetallesCaja(codigoCaja, page + 1, rowsPerPage);
    }
  };

  const mediosPagoUnicos = useMemo(() => {
    if (!detallesCaja?.items) return [];
    const medios = new Set<string>();
    detallesCaja.items.forEach(item => {
      if (item.nombreMCP) {
        medios.add(item.nombreMCP);
      }
    });
    return Array.from(medios).sort();
  }, [detallesCaja]);

  const movimientosConSaldo = useMemo(() => {
    if (!detallesCaja?.items || !informacionCaja) return [];
    
    const movimientos = [...detallesCaja.items];
    movimientos.sort((a, b) => {
      const dateA = new Date(a.fechaEmision).getTime();
      const dateB = new Date(b.fechaEmision).getTime();
      return dateA - dateB;
    });

    let saldoAcumulado = informacionCaja.saldoInicial;
    return movimientos.map(mov => {
      if (mov.tipoMovimiento?.toUpperCase() === 'INGRESO') {
        saldoAcumulado += Math.abs(mov.importe);
      } else if (mov.tipoMovimiento?.toUpperCase() === 'EGRESO') {
        saldoAcumulado -= Math.abs(mov.importe);
      }
      return {
        ...mov,
        saldoAcumulado
      };
    });
  }, [detallesCaja, informacionCaja]);

  const movimientosFiltrados = useMemo(() => {
    let filtrados = movimientosConSaldo;

    if (fechaDesde) {
      filtrados = filtrados.filter(m => {
        const fechaMov = new Date(m.fechaEmision);
        const fechaDesdeDate = new Date(fechaDesde);
        fechaDesdeDate.setHours(0, 0, 0, 0);
        return fechaMov >= fechaDesdeDate;
      });
    }

    if (fechaHasta) {
      filtrados = filtrados.filter(m => {
        const fechaMov = new Date(m.fechaEmision);
        const fechaHastaDate = new Date(fechaHasta);
        fechaHastaDate.setHours(23, 59, 59, 999);
        return fechaMov <= fechaHastaDate;
      });
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtrados = filtrados.filter(m =>
        String(m.nroFormulario).toLowerCase().includes(search) ||
        (m.detalle || '').toLowerCase().includes(search) ||
        (m.usuario || '').toLowerCase().includes(search) ||
        (m.nombre || '').toLowerCase().includes(search) ||
        (m.nombreMCP || '').toLowerCase().includes(search) ||
        (m.nombreTipoFormulario || '').toLowerCase().includes(search)
      );
    }

    if (tipoFiltro !== 'TODOS') {
      filtrados = filtrados.filter(m => 
        m.tipoMovimiento?.toUpperCase() === tipoFiltro.toUpperCase()
      );
    }

    if (medioPagoFiltro) {
      filtrados = filtrados.filter(m => m.nombreMCP === medioPagoFiltro);
    }

    if (sortField) {
      filtrados.sort((a, b) => {
        const aVal: unknown = a[sortField];
        const bVal: unknown = b[sortField];
        if (aVal === undefined || aVal === null) return 1;
        if (bVal === undefined || bVal === null) return -1;
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          // Si son fechas en formato string, convertir a Date para comparar
          if (sortField === 'fechaEmision' || String(sortField).includes('fecha') || String(sortField).includes('Fecha')) {
            const dateA = new Date(aVal).getTime();
            const dateB = new Date(bVal).getTime();
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
          }
          const aStr: string = aVal;
          const bStr: string = bVal;
          return sortDirection === 'asc' 
            ? aStr.localeCompare(bStr)
            : bStr.localeCompare(aStr);
        }
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          const aNum: number = aVal;
          const bNum: number = bVal;
          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }
        return 0;
      });
    }

    if (sortField === 'fechaEmision') {
      filtrados.sort((a, b) => {
        const dateA = new Date(a.fechaEmision).getTime();
        const dateB = new Date(b.fechaEmision).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }

    return filtrados;
  }, [movimientosConSaldo, fechaDesde, fechaHasta, searchTerm, tipoFiltro, medioPagoFiltro, sortField, sortDirection]);

  // Filtrar los movimientos de la página actual (el backend ya filtra por tipo de movimiento según el tab)
  const movimientosPorTab = useMemo(() => {
    if (!detallesCaja?.items || !informacionCaja) return [];
    const items = detallesCaja.items;
    
    // Aplicar filtros de fecha, búsqueda, etc. (el tipo de movimiento ya está filtrado por el backend)
    let filtrados = items;
    
    if (fechaDesde) {
      filtrados = filtrados.filter(m => {
        const fechaMov = new Date(m.fechaEmision);
        const fechaDesdeDate = new Date(fechaDesde);
        return fechaMov >= fechaDesdeDate;
      });
    }
    if (fechaHasta) {
      filtrados = filtrados.filter(m => {
        const fechaMov = new Date(m.fechaEmision);
        fechaMov.setHours(23, 59, 59, 999);
        const fechaHastaDate = new Date(fechaHasta);
        fechaHastaDate.setHours(23, 59, 59, 999);
        return fechaMov <= fechaHastaDate;
      });
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtrados = filtrados.filter(m =>
        m.detalle?.toLowerCase().includes(term) ||
        m.nombre?.toLowerCase().includes(term) ||
        m.nroFormulario?.toString().includes(term) ||
        m.nombreMCP?.toLowerCase().includes(term)
      );
    }
    if (tipoFiltro && tipoFiltro !== 'TODOS') {
      filtrados = filtrados.filter(m => m.tipoMovimiento?.toUpperCase() === tipoFiltro);
    }
    if (medioPagoFiltro && medioPagoFiltro !== 'TODOS') {
      filtrados = filtrados.filter(m => m.nombreMCP === medioPagoFiltro);
    }
    
    // Ordenar por fecha para calcular el saldo acumulado correctamente
    filtrados.sort((a, b) => {
      const dateA = new Date(a.fechaEmision).getTime();
      const dateB = new Date(b.fechaEmision).getTime();
      return dateA - dateB;
    });
    
    // Calcular saldo acumulado
    // Para calcular correctamente el saldo acumulado, necesitamos considerar:
    // 1. El saldo inicial de la caja
    // 2. Todos los movimientos anteriores a esta página (si los tenemos)
    // 3. Los movimientos de la página actual
    
    let saldoAcumulado = informacionCaja.saldoInicial;
    
    // Si tenemos movimientos anteriores, calcular el saldo acumulado hasta el inicio de esta página
    if (movimientosAnteriores.length > 0) {
      movimientosAnteriores.forEach(mov => {
        if (mov.tipoMovimiento?.toUpperCase() === 'INGRESO') {
          saldoAcumulado += Math.abs(mov.importe);
        } else if (mov.tipoMovimiento?.toUpperCase() === 'EGRESO') {
          saldoAcumulado -= Math.abs(mov.importe);
        }
      });
    }
    
    // Ahora calcular el saldo acumulado para los movimientos de la página actual
    return filtrados.map(mov => {
      if (mov.tipoMovimiento?.toUpperCase() === 'INGRESO') {
        saldoAcumulado += Math.abs(mov.importe);
      } else if (mov.tipoMovimiento?.toUpperCase() === 'EGRESO') {
        saldoAcumulado -= Math.abs(mov.importe);
      }
      return {
        ...mov,
        saldoAcumulado
      };
    });
  }, [detallesCaja, informacionCaja, movimientosAnteriores, fechaDesde, fechaHasta, searchTerm, tipoFiltro, medioPagoFiltro]);

  // Total de movimientos según el tab seleccionado
  // Si hay detalles cargados, usar el totalCount del backend (ya filtrado por tipo de movimiento)
  // Si no, usar los contadores del endpoint informacion
  const totalMovimientosTab = useMemo(() => {
    // Si hay detalles cargados y estamos en un tab específico, usar el totalCount del backend
    if (detallesCaja && currentTab !== 0) {
      return detallesCaja.totalCount;
    }
    
    // Para "Todos" o cuando no hay detalles, usar los contadores del endpoint informacion
    if (!informacionCaja) return 0;
    if (currentTab === 0) {
      return informacionCaja.cantidadMovimientosTotal ?? 0;
    } else if (currentTab === 1) {
      return informacionCaja.cantidadMovimientosIngresos ?? 0;
    } else if (currentTab === 2) {
      return informacionCaja.cantidadMovimientosEgresos ?? 0;
    }
    return 0;
  }, [informacionCaja, detallesCaja, currentTab]);

  const handleSort = (field: keyof MovimientoCaja) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleArqueoExpandido = (key: string) => {
    setArqueoExpandido(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleExportar = async () => {
    if (!codigoCaja || typeof codigoCaja !== 'number' || !informacionCaja) {
      enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      enqueueSnackbar('Obteniendo todos los movimientos para exportar...', { variant: 'info' });

      // Determinar el tipo de movimiento según el tab seleccionado
      let tipoMovimiento: 'Ingreso' | 'Egreso' | undefined = undefined;
      if (currentTab === 1) {
        tipoMovimiento = 'Ingreso';
      } else if (currentTab === 2) {
        tipoMovimiento = 'Egreso';
      }

      // Obtener el total de movimientos según el tab
      const totalMovimientos = 
        currentTab === 0 ? (informacionCaja.cantidadMovimientosTotal || 0) :
        currentTab === 1 ? (informacionCaja.cantidadMovimientosIngresos || 0) :
        (informacionCaja.cantidadMovimientosEgresos || 0);

      if (totalMovimientos === 0) {
        enqueueSnackbar('No hay movimientos para exportar', { variant: 'warning' });
        setLoading(false);
        return;
      }

      // Obtener todos los movimientos paginando
      const pageSize = 50; // Máximo permitido por el backend
      const totalPages = Math.ceil(totalMovimientos / pageSize);
      const allMovimientos: MovimientoCaja[] = [];

      for (let page = 1; page <= totalPages; page++) {
        const response = await tesoreriaCajaService.getDetalleCaja(
          codigoCaja,
          page,
          pageSize,
          tipoMovimiento
        );
        allMovimientos.push(...response.items);
      }

      // Aplicar filtros del cliente (fecha, búsqueda, medio de pago)
      let movimientosFiltrados = allMovimientos;

      if (fechaDesde) {
        const fechaDesdeDate = new Date(fechaDesde);
        fechaDesdeDate.setHours(0, 0, 0, 0);
        movimientosFiltrados = movimientosFiltrados.filter(m => {
          const fechaMov = new Date(m.fechaEmision);
          return fechaMov >= fechaDesdeDate;
        });
      }

      if (fechaHasta) {
        const fechaHastaDate = new Date(fechaHasta);
        fechaHastaDate.setHours(23, 59, 59, 999);
        movimientosFiltrados = movimientosFiltrados.filter(m => {
          const fechaMov = new Date(m.fechaEmision);
          return fechaMov <= fechaHastaDate;
        });
      }

      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        movimientosFiltrados = movimientosFiltrados.filter(m =>
          String(m.nroFormulario).toLowerCase().includes(search) ||
          (m.detalle || '').toLowerCase().includes(search) ||
          (m.usuario || '').toLowerCase().includes(search) ||
          (m.nombre || '').toLowerCase().includes(search) ||
          (m.nombreMCP || '').toLowerCase().includes(search) ||
          (m.nombreTipoFormulario || '').toLowerCase().includes(search)
        );
      }

      if (medioPagoFiltro) {
        movimientosFiltrados = movimientosFiltrados.filter(m => m.nombreMCP === medioPagoFiltro);
      }

      // Ordenar por fecha
      movimientosFiltrados.sort((a, b) => {
        const dateA = new Date(a.fechaEmision).getTime();
        const dateB = new Date(b.fechaEmision).getTime();
        return dateA - dateB;
      });

      // Calcular saldo acumulado
      let saldoAcumulado = informacionCaja.saldoInicial;
      const movimientosConSaldo = movimientosFiltrados.map(mov => {
        if (mov.tipoMovimiento?.toUpperCase() === 'INGRESO') {
          saldoAcumulado += Math.abs(mov.importe);
        } else if (mov.tipoMovimiento?.toUpperCase() === 'EGRESO') {
          saldoAcumulado -= Math.abs(mov.importe);
        }
        return {
          ...mov,
          saldoAcumulado
        };
      });

      if (movimientosConSaldo.length === 0) {
        enqueueSnackbar('No hay movimientos que coincidan con los filtros aplicados', { variant: 'warning' });
        setLoading(false);
        return;
      }

      // Preparar datos para exportar
      const datos = movimientosConSaldo.map(m => ({
        'Medio de Cobro': m.nombreMCP || '',
        'Fecha': formatDate(m.fechaEmision),
        'Comprobante': m.nombreTipoFormulario ? `${m.nombreTipoFormulario} ${m.nroFormulario}` : String(m.nroFormulario),
        'Tipo': m.nombreTipoFormulario || '',
        'Cartera': m.nombreCartera || '',
        'Detalle': m.detalle || '',
        'Debe': m.tipoMovimiento?.toUpperCase() === 'INGRESO' ? m.importe : 0,
        'Haber': m.tipoMovimiento?.toUpperCase() === 'EGRESO' ? Math.abs(m.importe) : 0,
        'Saldo': (m as any).saldoAcumulado || 0,
        'Usuario': m.usuario || '',
        'Entidad': m.entidad || 0,
      }));

      const ws = XLSX.utils.json_to_sheet(datos);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Movimientos');

      if (informacionCaja) {
        const infoSheet = XLSX.utils.json_to_sheet([{
          'Código de Caja': informacionCaja.codigoCaja,
          'Nombre': informacionCaja.nombre,
          'Estado': informacionCaja.estado,
          'Fecha Apertura': formatDate(informacionCaja.fechaApertura),
          'Fecha Cierre': formatDate(informacionCaja.fechaCierre || ''),
          'Usuario Responsable': informacionCaja.usuarioResponsable,
          'Saldo Inicial': informacionCaja.saldoInicial,
          'Total Ingresos': informacionCaja.totalIngresos,
          'Total Egresos': informacionCaja.totalEgresos,
          'Saldo Final': informacionCaja.saldoFinal,
        }]);
        XLSX.utils.book_append_sheet(wb, infoSheet, 'Información Caja');
      }

      const fileName = `Movimientos_Caja_${informacionCaja?.codigoCaja || 'N'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      enqueueSnackbar(`Datos exportados exitosamente (${movimientosConSaldo.length} movimientos)`, { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar:', error);
      enqueueSnackbar('Error al exportar los datos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };


  const handleLimpiarFiltros = () => {
    setFechaDesde('');
    setFechaHasta('');
    setSearchTerm('');
    setTipoFiltro('TODOS');
    setMedioPagoFiltro('');
    setCurrentTab(0);
  };

  const formatComprobante = (mov: MovimientoCaja) => {
    if (mov.nombreTipoFormulario) {
      return `${mov.nombreTipoFormulario} ${mov.nroFormulario}`;
    }
    return String(mov.nroFormulario);
  };

  const handleClickMovimiento = (movimiento: MovimientoCaja) => {
    setSelectedMovimiento(movimiento);
    setOpenDetails(true);
  };

  const handleFiltrosRapidos = (tipo: 'hoy' | 'semana' | 'mes' | '7dias') => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const finHoy = new Date();
    finHoy.setHours(23, 59, 59, 999);

    switch (tipo) {
      case 'hoy':
        setFechaDesde(hoy.toISOString().split('T')[0]);
        setFechaHasta(hoy.toISOString().split('T')[0]);
        break;
      case 'semana':
        const inicioSemana = new Date(hoy);
        inicioSemana.setDate(hoy.getDate() - hoy.getDay());
        setFechaDesde(inicioSemana.toISOString().split('T')[0]);
        setFechaHasta(finHoy.toISOString().split('T')[0]);
        break;
      case 'mes':
        const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        setFechaDesde(inicioMes.toISOString().split('T')[0]);
        setFechaHasta(finHoy.toISOString().split('T')[0]);
        break;
      case '7dias':
        const hace7Dias = new Date(hoy);
        hace7Dias.setDate(hoy.getDate() - 7);
        setFechaDesde(hace7Dias.toISOString().split('T')[0]);
        setFechaHasta(finHoy.toISOString().split('T')[0]);
        break;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" mb={3}>
            <TextField
              label="Código de Caja"
              type="number"
              value={codigoCaja}
              onChange={(e) => setCodigoCaja(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{ flexGrow: { xs: 1, md: 0 }, minWidth: { md: 200 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={handleAbrirBuscarCajaDialogDefault}
                    />
                  </InputAdornment>
                ),
              }}
            />
            {codigoCaja && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                }}
              >
                Actualizar
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleIrCajaAbierta}
              disabled={loading}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
              }}
            >
              Ir a caja abierta
            </Button>
          </Stack>

          {informacionCaja && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                    {informacionCaja.nombre}
                  </Typography>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Typography variant="body1" color="text.secondary">
                        <strong>Código:</strong> {informacionCaja.codigoCaja.toLocaleString('es-AR')}
                      </Typography>
                      {(() => {
                        const estadoRaw = informacionCaja.estado || '';
                        const estadoTrimmed = estadoRaw.trim();
                        const estadoUpper = estadoTrimmed.toUpperCase();
                        const esAbierto = estadoUpper === 'ABIERTO';
                        return (
                          <Chip
                            label={estadoTrimmed}
                            icon={
                              esAbierto
                                ? <LockOpenIcon />
                                : estadoUpper === 'CERRADO'
                                ? <LockIcon />
                                : undefined
                            }
                            size="small"
                            sx={{
                              ...(esAbierto && {
                                bgcolor: '#4caf50 !important',
                                color: '#fff !important',
                                '& .MuiChip-icon': {
                                  color: '#fff !important',
                                },
                              }),
                              ...(!esAbierto && {
                                bgcolor: '#9e9e9e',
                                color: '#fff',
                              }),
                            }}
                          />
                        );
                      })()}
                    </Stack>
                    {grupoNombre && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Grupo:</strong> {grupoNombre}
                      </Typography>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Fecha Apertura:</strong> {formatDate(informacionCaja.fechaApertura)}
                    </Typography>
                    {informacionCaja.fechaCierre && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Fecha Cierre:</strong> {formatDate(informacionCaja.fechaCierre)}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      <AccountCircleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      <strong>Usuario:</strong> {informacionCaja.usuarioResponsable}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </>
          )}

          {informacionCaja && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Card sx={{ bgcolor: '#757575', color: '#fff', textAlign: 'center', py: 2 }}>
                    <AccountBalanceIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(informacionCaja.saldoInicial)}
                    </Typography>
                    <Typography variant="caption">Saldo Inicial</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Card sx={{ bgcolor: '#4caf50', color: '#fff', textAlign: 'center', py: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(informacionCaja.totalIngresos)}
                    </Typography>
                    <Typography variant="caption">Total Ingresos</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Card sx={{ bgcolor: '#f44336', color: '#fff', textAlign: 'center', py: 2 }}>
                    <TrendingDownIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(informacionCaja.totalEgresos)}
                    </Typography>
                    <Typography variant="caption">Total Egresos</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Card sx={{ bgcolor: '#1976d2', color: '#fff', textAlign: 'center', py: 2 }}>
                    <AccountBalanceIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(informacionCaja.saldoFinal)}
                    </Typography>
                    <Typography variant="caption">Saldo Final</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Card sx={{ bgcolor: '#2196f3', color: '#fff', textAlign: 'center', py: 2 }}>
                    <CalculateIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(informacionCaja.totalIngresos - informacionCaja.totalEgresos)}
                    </Typography>
                    <Typography variant="caption">Diferencia Neta</Typography>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}

        </CardContent>
      </Card>

      {/* Contenedor principal con Tabs grandes */}
      {informacionCaja && (
        <Card sx={{ mt: 3, borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={mainTab}
                onChange={(e, newValue) => setMainTab(newValue)}
                aria-label="tabs principales"
                sx={{
                  '& .MuiTab-root': {
                    fontSize: '1rem',
                    fontWeight: 600,
                    minHeight: 48,
                  },
                }}
              >
                <Tab label="Información de Arqueo" />
                <Tab label="Detalle de Movimientos" />
              </Tabs>
            </Box>

            {/* Tab de Arqueo */}
            {mainTab === 0 && (
              <Box>
                {loadingArqueo ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : arqueoCaja ? (
                  <Box>
                    {/* Por Tipo de Pago */}
                    {arqueoCaja.porTipoPago && arqueoCaja.porTipoPago.length > 0 && (() => {
                      const key = 'porTipoPago';
                      const items = arqueoCaja.porTipoPago;
                      const isExpanded = arqueoExpandido[key] || false;
                      const mostrarTodos = items.length <= 10 || isExpanded;
                      const itemsAMostrar = mostrarTodos ? items : items.slice(0, 10);
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Por Tipo de Pago
                            </Typography>
                            {items.length > 10 && (
                              <Button
                                size="small"
                                onClick={() => toggleArqueoExpandido(key)}
                                endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{ minWidth: 'auto', padding: '4px 8px', ml: 0 }}
                              >
                                {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                              </Button>
                            )}
                          </Box>
                          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Tipo de Pago</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{item.tipoPago?.trim() || '-'}</TableCell>
                                    <TableCell>{item.descripcion?.trim() || '-'}</TableCell>
                                    <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                      {formatCurrency(item.totalIngresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                      {formatCurrency(item.totalEgresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(item.saldo)}
                                    </TableCell>
                                    <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      );
                    })()}

                    {/* Por MCP */}
                    {arqueoCaja.porMcp && arqueoCaja.porMcp.length > 0 && (() => {
                      const key = 'porMcp';
                      const items = arqueoCaja.porMcp;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Por Medio de Cobro/Pago
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>MCP</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Nombre MCP</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{item.mcp?.trim() || '-'}</TableCell>
                                    <TableCell>{item.nombreMcp?.trim() || '-'}</TableCell>
                                    <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                      {formatCurrency(item.totalIngresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                      {formatCurrency(item.totalEgresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(item.saldo)}
                                    </TableCell>
                                    <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Por Cartera */}
                    {arqueoCaja.porCartera && arqueoCaja.porCartera.length > 0 && (() => {
                      const key = 'porCartera';
                      const items = arqueoCaja.porCartera;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Por Cartera
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                              <Table>
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Cartera</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Nombre Cartera</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {items.map((item, idx) => (
                                    <TableRow key={idx} hover>
                                      <TableCell>{item.cartera}</TableCell>
                                      <TableCell>{item.nombreCartera?.trim() || '-'}</TableCell>
                                      <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                        {formatCurrency(item.totalIngresos)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                        {formatCurrency(item.totalEgresos)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(item.saldo)}
                                      </TableCell>
                                      <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Por Tipo de Formulario */}
                    {arqueoCaja.porTipoFormulario && arqueoCaja.porTipoFormulario.length > 0 && (() => {
                      const key = 'porTipoFormulario';
                      const items = arqueoCaja.porTipoFormulario;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Por Tipo de Formulario
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Tipo Formulario</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Nombre Tipo Formulario</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{item.tipoFormulario?.trim() || '-'}</TableCell>
                                    <TableCell>{item.nombreTipoFormulario?.trim() || '-'}</TableCell>
                                    <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                      {formatCurrency(item.totalIngresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                      {formatCurrency(item.totalEgresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(item.saldo)}
                                    </TableCell>
                                    <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Por Usuario */}
                    {arqueoCaja.porUsuario && arqueoCaja.porUsuario.length > 0 && (() => {
                      const key = 'porUsuario';
                      const items = arqueoCaja.porUsuario;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Por Usuario
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{item.usuario?.trim() || '-'}</TableCell>
                                    <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                      {formatCurrency(item.totalIngresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                      {formatCurrency(item.totalEgresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(item.saldo)}
                                    </TableCell>
                                    <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Por Entidad */}
                    {arqueoCaja.porEntidad && arqueoCaja.porEntidad.length > 0 && (() => {
                      const key = 'porEntidad';
                      const items = arqueoCaja.porEntidad;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Por Entidad
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Entidad</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{item.entidad}</TableCell>
                                    <TableCell>{item.nombre?.trim() || '-'}</TableCell>
                                    <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                      {formatCurrency(item.totalIngresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                      {formatCurrency(item.totalEgresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(item.saldo)}
                                    </TableCell>
                                    <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Por Fecha */}
                    {arqueoCaja.porFecha && arqueoCaja.porFecha.length > 0 && (() => {
                      const key = 'porFecha';
                      const items = arqueoCaja.porFecha;
                      const isExpanded = arqueoExpandido[key] || false;
                      return (
                        <Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Por Fecha
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => toggleArqueoExpandido(key)}
                              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {isExpanded ? 'Contraer' : `Ver todos (${items.length})`}
                            </Button>
                          </Stack>
                          {isExpanded && (
                            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Ingresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Egresos</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cant. Movimientos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell>{formatDate(item.fechaEmision)}</TableCell>
                                    <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                      {formatCurrency(item.totalIngresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                                      {formatCurrency(item.totalEgresos)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(item.saldo)}
                                    </TableCell>
                                    <TableCell align="right">{item.cantidadMovimientos}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No hay información de arqueo disponible
                  </Typography>
                )}
              </Box>
            )}

            {/* Tab de Movimientos */}
            {mainTab === 1 && (
              <Box>
                {/* Filtros */}
                <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Sección de Fechas */}
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <TextField
                          label="Fecha Desde"
                          type="date"
                          value={fechaDesde}
                          onChange={(e) => setFechaDesde(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ flex: 1, minWidth: 140 }}
                        />
                        <TextField
                          label="Fecha Hasta"
                          type="date"
                          value={fechaHasta}
                          onChange={(e) => setFechaHasta(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ flex: 1, minWidth: 140 }}
                        />
                      </Stack>
                    </Grid>

                    {/* Botones de Filtros Rápidos */}
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleFiltrosRapidos('hoy')}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          HOY
                        </Button>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleFiltrosRapidos('semana')}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          ESTA SEMANA
                        </Button>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleFiltrosRapidos('mes')}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          ESTE MES
                        </Button>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleFiltrosRapidos('7dias')}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          7 DÍAS
                        </Button>
                      </Stack>
                    </Grid>

                    {/* Búsqueda */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Buscar"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>

                    {/* Filtros de Tipo y Medio de Pago */}
                    <Grid item xs={12} md={6}>
                      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                          <InputLabel>Tipo</InputLabel>
                          <Select
                            value={tipoFiltro}
                            label="Tipo"
                            onChange={(e) => setTipoFiltro(e.target.value as 'TODOS' | 'INGRESO' | 'EGRESO')}
                          >
                            <MenuItem value="TODOS">Todos</MenuItem>
                            <MenuItem value="INGRESO">Ingresos</MenuItem>
                            <MenuItem value="EGRESO">Egresos</MenuItem>
                          </Select>
                        </FormControl>
                        {mediosPagoUnicos.length > 0 && (
                          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1 }}>
                            <InputLabel>Medio de Pago</InputLabel>
                            <Select
                              value={medioPagoFiltro}
                              label="Medio de Pago"
                              onChange={(e) => setMedioPagoFiltro(e.target.value)}
                            >
                              <MenuItem value="">Todos</MenuItem>
                              {mediosPagoUnicos.map(medio => (
                                <MenuItem key={medio} value={medio}>{medio}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Stack>
                    </Grid>

                    {/* Botones de Acción */}
                    <Grid item xs={12} md={6}>
                      <Stack 
                        direction="row" 
                        spacing={1} 
                        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<FilterListIcon />}
                          onClick={() => setOpenFilters(!openFilters)}
                        >
                          FILTRAR
                        </Button>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={handleLimpiarFiltros}
                        >
                          LIMPIAR
                        </Button>
                        <Button 
                          size="small" 
                          variant="contained" 
                          color="primary"
                          startIcon={<GetAppIcon />} 
                          onClick={handleExportar}
                        >
                          EXPORTAR
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Tabs de movimientos (Todos/Ingresos/Egresos) */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs
                    value={currentTab}
                    onChange={(e, newValue) => setCurrentTab(newValue)}
                    aria-label="tabs de movimientos"
                  >
                    <Tab
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>Todos</span>
                          <Chip
                            label={informacionCaja?.cantidadMovimientosTotal ?? 0}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      }
                    />
                    <Tab
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ArrowUpwardIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                          <span>Ingresos</span>
                          <Chip
                            label={informacionCaja?.cantidadMovimientosIngresos ?? 0}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      }
                    />
                    <Tab
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ArrowDownwardIcon sx={{ fontSize: 16, color: '#f44336' }} />
                          <span>Egresos</span>
                          <Chip
                            label={informacionCaja?.cantidadMovimientosEgresos ?? 0}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      }
                    />
                  </Tabs>
                </Box>

                {/* Contenido de movimientos */}
                {loadingDetalles ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {isMobile ? (
                      <Stack spacing={2}>
                        {movimientosPorTab.map((mov, idx) => {
                          const esIngreso = mov.tipoMovimiento?.toUpperCase() === 'INGRESO';
                          return (
                            <Card
                              key={`${mov.nroFormulario}-${idx}`}
                              sx={{
                                borderLeft: `4px solid ${esIngreso ? '#4caf50' : '#f44336'}`,
                                bgcolor: esIngreso ? '#e8f5e9' : '#ffebee',
                                '&:hover': { boxShadow: 3, cursor: 'pointer' }
                              }}
                              onClick={() => handleClickMovimiento(mov)}
                            >
                              <CardContent>
                                <Stack spacing={1}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2" fontWeight={600}>
                                      {formatDate(mov.fechaEmision)}
                                    </Typography>
                                    <Chip
                                      label={mov.tipoMovimiento}
                                      size="small"
                                      sx={{
                                        bgcolor: esIngreso ? '#4caf50' : '#f44336',
                                        color: '#fff',
                                        height: 20,
                                        fontSize: '0.7rem'
                                      }}
                                      icon={esIngreso ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />}
                                    />
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Medio:</strong> {mov.nombreMCP || '-'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Comprobante:</strong> {formatComprobante(mov)}
                                  </Typography>
                                  <Typography variant="body2">
                                    {mov.detalle || '-'}
                                  </Typography>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: esIngreso ? '#4caf50' : '#f44336' }}>
                                      {formatCurrency(Math.abs(mov.importe))}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Saldo: {formatCurrency((mov as any).saldoAcumulado || 0)}
                                    </Typography>
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </Card>
                          );
                        })}
                        {movimientosPorTab.length === 0 && (
                          <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                            No hay movimientos para mostrar
                          </Typography>
                        )}
                        <TablePagination
                          component="div"
                          count={totalMovimientosTab}
                          page={page}
                          onPageChange={(e, newPage) => setPage(newPage)}
                          rowsPerPage={rowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                          }}
                          rowsPerPageOptions={[10]}
                          labelRowsPerPage="Filas por página"
                          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                        />
                      </Stack>
                    ) : (
                      <>
                        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                          <Table>
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Medio de Cobro</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Cartera</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Detalle</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Debe</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Haber</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {movimientosPorTab.map((mov, idx) => {
                                const esIngreso = mov.tipoMovimiento?.toUpperCase() === 'INGRESO';
                                return (
                                  <TableRow
                                    key={`${mov.nroFormulario}-${idx}`}
                                    sx={{
                                      bgcolor: esIngreso ? '#e8f5e9' : '#ffebee',
                                      '&:hover': { bgcolor: esIngreso ? '#c8e6c9' : '#ffcdd2', cursor: 'pointer' }
                                    }}
                                    onClick={() => handleClickMovimiento(mov)}
                                  >
                                    <TableCell>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        {esIngreso ? (
                                          <ArrowUpwardIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                        ) : (
                                          <ArrowDownwardIcon sx={{ fontSize: 16, color: '#f44336' }} />
                                        )}
                                        <span>{mov.nombreMCP || '-'}</span>
                                      </Stack>
                                    </TableCell>
                                    <TableCell>{formatDate(mov.fechaEmision)}</TableCell>
                                    <TableCell>{formatComprobante(mov)}</TableCell>
                                    <TableCell>{mov.nombreTipoFormulario || '-'}</TableCell>
                                    <TableCell>{mov.nombreCartera || '-'}</TableCell>
                                    <TableCell>{mov.detalle || '-'}</TableCell>
                                    <TableCell align="right">
                                      {esIngreso ? formatCurrency(mov.importe) : '-'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {!esIngreso ? formatCurrency(Math.abs(mov.importe)) : '-'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency((mov as any).saldoAcumulado || 0)}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                          {movimientosPorTab.length === 0 && (
                            <Box sx={{ py: 4 }}>
                              <Typography align="center" color="text.secondary">
                                No hay movimientos para mostrar
                              </Typography>
                            </Box>
                          )}
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={totalMovimientosTab}
                          page={page}
                          onPageChange={(e, newPage) => setPage(newPage)}
                          rowsPerPage={rowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                          }}
                          rowsPerPageOptions={[10]}
                          labelRowsPerPage="Filas por página"
                          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                        />
                      </>
                    )}
                  </>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Detalles del Movimiento
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedMovimiento && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Número de Formulario:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.nroFormulario}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tipo de Formulario:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.nombreTipoFormulario || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Fecha y Hora:</strong>
                  </Typography>
                  <Typography variant="body1">{formatDate(selectedMovimiento.fechaEmision)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tipo de Movimiento:</strong>
                  </Typography>
                  <Chip
                    label={selectedMovimiento.tipoMovimiento}
                    sx={{
                      bgcolor: selectedMovimiento.tipoMovimiento?.toUpperCase() === 'INGRESO' ? '#4caf50' : '#f44336',
                      color: '#fff',
                      mt: 0.5
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Usuario:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.usuario || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Entidad:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.entidad || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Medio de Cobro/Pago:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.nombreMCP || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tipo de Pago:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.tipoPago || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Cartera:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.nombreCartera || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Importe:</strong>
                  </Typography>
                  <Typography variant="h6" sx={{ color: selectedMovimiento.tipoMovimiento?.toUpperCase() === 'INGRESO' ? '#4caf50' : '#f44336' }}>
                    {formatCurrency(Math.abs(selectedMovimiento.importe))}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Detalle:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.detalle || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Nombre:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedMovimiento.nombre || '-'}</Typography>
                </Grid>
                {(selectedMovimiento as any).saldoAcumulado !== undefined && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Saldo Acumulado:</strong>
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency((selectedMovimiento as any).saldoAcumulado || 0)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de búsqueda de cajas */}
      <Dialog
        open={openBuscarCajaDialog}
        onClose={() => setOpenBuscarCajaDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Buscar caja</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Buscar por código o nombre"
                  value={buscarCajaTerm}
                  onChange={(e) => setBuscarCajaTerm(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={buscarCajaEstado}
                    label="Estado"
                    onChange={(e) => setBuscarCajaEstado(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="ABIERTO">ABIERTO</MenuItem>
                    <MenuItem value="CERRADO">CERRADO</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Grupo</InputLabel>
                  <Select
                    value={buscarCajaGrupo}
                    label="Grupo"
                    onChange={(e) => setBuscarCajaGrupo(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {buscarCajaGrupos.map((g) => (
                      <MenuItem key={g.id} value={String(g.codigo)}>
                        {g.codigo} - {g.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Fecha Apertura desde"
                  type="date"
                  value={buscarCajaFechaDesde}
                  onChange={(e) => setBuscarCajaFechaDesde(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Fecha Apertura hasta"
                  type="date"
                  value={buscarCajaFechaHasta}
                  onChange={(e) => setBuscarCajaFechaHasta(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Button
              variant="contained"
              onClick={handleBuscarCaja}
              disabled={buscarCajaLoading}
              sx={{ alignSelf: 'flex-start' }}
            >
              {buscarCajaLoading ? <CircularProgress size={22} /> : 'Buscar'}
            </Button>
            {buscarCajaLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Fecha Apertura</TableCell>
                      <TableCell>Fecha Cierre</TableCell>
                      <TableCell>Grupo</TableCell>
                      <TableCell>Nombre Grupo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {buscarCajaResultados.map((caja) => (
                      <TableRow
                        key={caja.codigoCaja}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleSeleccionarCaja(caja)}
                      >
                        <TableCell>{caja.codigoCaja.toLocaleString('es-AR')}</TableCell>
                        <TableCell>{caja.descripcion}</TableCell>
                        <TableCell>{caja.estado || '-'}</TableCell>
                        <TableCell>{caja.fechaApertura ? formatDate(caja.fechaApertura) : '-'}</TableCell>
                        <TableCell>{caja.fechaCierre ? formatDate(caja.fechaCierre) : '-'}</TableCell>
                        <TableCell>{caja.grupo ?? '-'}</TableCell>
                        <TableCell>{caja.grupoNombre || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {!buscarCajaResultados.length && !buscarCajaLoading && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">
                            Ingrese un criterio y presione &quot;Buscar&quot; para listar cajas.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[5, 10, 20]}
                  count={buscarCajaTotal}
                  rowsPerPage={buscarCajaRowsPerPage}
                  page={buscarCajaPage}
                  onPageChange={(_, newPage) => {
                    ejecutarBusquedaCajas(newPage, buscarCajaRowsPerPage);
                  }}
                  onRowsPerPageChange={(e) => {
                    const newSize = parseInt(e.target.value, 10);
                    setBuscarCajaRowsPerPage(newSize);
                    ejecutarBusquedaCajas(0, newSize);
                  }}
                  labelRowsPerPage="Filas por página"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                />
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBuscarCajaDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MovimientosCaja;

